// Add questions: a new one by type, from the shared library, or copied from another questionnaire.
import { formatSurveyNumber, questionTypeLabel, type Question, type QuestionType } from '@survey/core';
import { BookMarked, Copy, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, type LibraryQuestion } from '../../api/client';
import type { SurveyDetail, SurveySummary } from '../../api/types';
import { useAuth } from '../../auth';
import { useI18n } from '../../i18n';
import { Empty, Modal, useToast } from '../ui';
import { importedQuestion, newQuestion, TYPE_ICONS } from './model';
import { TypeGrid } from './TypePicker';

type Tab = 'new' | 'library' | 'copy';

export function AddQuestionModal({
  surveyId,
  sections,
  section,
  onSection,
  onAdd,
  onClose,
}: {
  surveyId: string;
  /** Sections the question can go into; every question lives in one. */
  sections: string[];
  section: string;
  onSection: (title: string) => void;
  onAdd: (qs: Question[]) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('new');
  return (
    <Modal title={t('builder.add_a_question')} onClose={onClose} wide>
      <div className="field mb-16">
        <label htmlFor="add-section">{t('builder.add_to_section')}</label>
        <select id="add-section" className="select" value={section} onChange={(e) => onSection(e.target.value)}>
          {sections.map((title) => (
            <option key={title} value={title}>
              {title}
            </option>
          ))}
        </select>
      </div>
      <div className="seg mb-16" role="tablist">
        {(['new', 'library', 'copy'] as Tab[]).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {t(`library.tab_${k}`)}
          </button>
        ))}
      </div>
      {tab === 'new' && <TypeGrid onPick={(type: QuestionType) => onAdd([newQuestion(type)])} />}
      {tab === 'library' && <LibraryTab onAdd={onAdd} />}
      {tab === 'copy' && <CopyTab surveyId={surveyId} onAdd={onAdd} />}
    </Modal>
  );
}

function QuestionLine({ q, extra }: { q: Question; extra?: React.ReactNode }) {
  const { t, locale } = useI18n();
  const Icon = TYPE_ICONS[q.type];
  return (
    <>
      <span className="tile sm">
        <Icon />
      </span>
      <span className="grow">
        <span className="ellipsis" style={{ display: 'block' }}>
          {q.questionCode && <span className="pill" style={{ marginRight: 6 }}>{q.questionCode}</span>}
          {q.title || t('common.untitled_question')}
        </span>
        <span className="small muted">
          {questionTypeLabel(q.type, locale)}
          {extra}
        </span>
      </span>
    </>
  );
}

function LibraryTab({ onAdd }: { onAdd: (qs: Question[]) => void }) {
  const { t } = useI18n();
  const { user, can } = useAuth();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<LibraryQuestion[] | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => api.library(search).then((r) => setItems(r.questions), () => setItems([])), 250);
    return () => clearTimeout(timer);
  }, [search]);
  return (
    <div className="stack gap-12">
      <label className="search">
        <Search aria-hidden />
        <input className="input" value={search} placeholder={t('library.search')} aria-label={t('library.search')} onChange={(e) => setSearch(e.target.value)} autoFocus />
      </label>
      {items === null ? (
        <Loader2 className="spin" />
      ) : items.length === 0 ? (
        <Empty icon={<BookMarked />} title={search ? t('library.no_match') : t('library.empty')}>
          <p className="small">{t('library.empty_hint')}</p>
        </Empty>
      ) : (
        <div className="pick-list">
          {items.map((item) => (
            <div key={item.id} className="pick-item">
              <QuestionLine q={item.question} extra={<> · {t('library.saved_by', { name: item.ownerName })}</>} />
              <button className="btn sm" onClick={() => onAdd([importedQuestion(item.question)])}>
                <Plus /> {t('common.add')}
              </button>
              {(item.ownerId === user?.id || can('library.manage_all')) && (
                <button
                  className="btn ghost sm icon"
                  aria-label={t('library.remove')}
                  onClick={() =>
                    api.deleteFromLibrary(item.id).then(
                      () => setItems((list) => list?.filter((x) => x.id !== item.id) ?? null),
                      (e) => toast(e instanceof Error ? e.message : t('common.action_failed'), 'error'),
                    )
                  }
                >
                  <Trash2 />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CopyTab({ surveyId, onAdd }: { surveyId: string; onAdd: (qs: Question[]) => void }) {
  const { t } = useI18n();
  const [surveys, setSurveys] = useState<SurveySummary[] | null>(null);
  const [source, setSource] = useState<SurveyDetail | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  useEffect(() => {
    api.listSurveys({}).then((r) => setSurveys(r.surveys.filter((s) => s.id !== surveyId)), () => setSurveys([]));
  }, [surveyId]);
  const choose = (id: string) => {
    setPicked(new Set());
    setSource(null);
    if (id) api.getSurvey(id).then(setSource, () => setSource(null));
  };
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const questions = source?.definition.questions ?? [];
  return (
    <div className="stack gap-12">
      <select className="select" defaultValue="" onChange={(e) => choose(e.target.value)} aria-label={t('library.pick_survey')}>
        <option value="">{surveys === null ? t('common.loading') : t('library.pick_survey')}</option>
        {surveys?.map((s) => (
          <option key={s.id} value={s.id}>
            {formatSurveyNumber(s.number)} · {s.title || t('common.untitled_questionnaire')}
          </option>
        ))}
      </select>
      {source && (
        <>
          <div className="row gap-8">
            <span className="small muted grow">{t('library.copy_hint')}</span>
            <button className="btn ghost sm" onClick={() => setPicked(picked.size === questions.length ? new Set() : new Set(questions.map((q) => q.id)))}>
              {picked.size === questions.length ? t('library.select_none') : t('library.select_all')}
            </button>
          </div>
          <div className="pick-list">
            {questions.map((q) => (
              <label key={q.id} className={`pick-item ${picked.has(q.id) ? 'on' : ''}`}>
                <input type="checkbox" checked={picked.has(q.id)} onChange={() => toggle(q.id)} />
                <QuestionLine q={q} />
              </label>
            ))}
          </div>
          <div className="btn-row">
            <button className="btn primary" disabled={!picked.size} onClick={() => onAdd(questions.filter((q) => picked.has(q.id)).map(importedQuestion))}>
              <Copy /> {t('library.copy_n', { count: picked.size })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
